import { ISODateToLocalizedShortDate, localizeLink } from 'client/javascript/util/locale';
import { KEYS as k } from 'server/util/inventory_config';
import { __ } from 'client/javascript/messages';
import { createElement, escapeHtml as h } from 'client/javascript/util/dom_utils';
import { varsFromServer } from 'client/javascript/client_side_injection_vars';

const HOT_DESKS = 'Hot Desks';
const DEDICATED_DESKS = 'Dedicated Desks';
const PRIVATE_OFFICES = 'Private Offices';
const productTypeToMessageKeyMap = {
  [HOT_DESKS]: 'hotDesks',
  [DEDICATED_DESKS]: 'dedicatedDesks',
  [PRIVATE_OFFICES]: 'privateOffices'
};

export function buildBuildingCard({
  building,
  filtersValues = {},
  needsToFilter,
  emptyHeartIconPath,
  fullHeartIconPath,
  savingExperimentIsLive = false
}) {
  const imageUrl = building[k.image_url];
  const imgixOptions = `${imageUrl.includes('?') ? '&' : '?'}q=50&auto=format%20compress`;
  const url = `${imageUrl}${imgixOptions}`;
  const html = `
    ${buildSaveIcon(building, savingExperimentIsLive, emptyHeartIconPath, fullHeartIconPath)}
    <div class="search-top-row ${needsToFilter ? 'show-prices' : ''}">
      <div class="ray-card__image ray-image ray-image--16by9 search-card__image">
        <img data-src="${url}&w=300" data-srcset="${url}&w=600 2x" alt="${h(building[k.name])}" />
      </div>
      ${buildOpenDateTag(building)}
      ${buildOnDemandTag(building)}
      <div class="ray-card__content building-content">
        <h3 class="ray-card__heading building-heading">${h(building[k.name])}</h3>
        <div class="building-market">${h(building[k.parent_name])}</div>
        ${buildAmenities(building)}
      </div>
    </div>
    ${buildPrices(building, filtersValues, needsToFilter)}
  `;

  const card = createElement('a', {
    className: 'search-card ray-card ray-card--row',
    attrs: {
      href: hrefBasedOnFilters(building, filtersValues.workspace, needsToFilter),
      'data-feature-id': building.feature_id,
      'data-imperfect-match': !!(building.imperfectDate || building.imperfectCapacity),
      'data-test-priority': building[k.priority],
      'data-building-id': building[k.id]
    },
    html
  });
  return card;
}

function hrefBasedOnFilters(building, workspace, needsToFilter) {
  if (needsToFilter && workspace && building[k.is_self_serve_building_and_available]) {
    return localizeLink({ to: `/buildings/${building[k.slug]}/reserve` });
  }
  return localizeLink({ to: `/buildings/${building[k.slug]}` });
}

function buildSaveIcon(building, savingExperimentIsLive, emptyHeartIconPath, fullHeartIconPath) {
  if (!savingExperimentIsLive) return '';

  return `
    <img data-src=${emptyHeartIconPath} class="lazy heart empty-heart-icon-circle" data-building-id=${
    building[k.id]
  } alt="Save heart" />
    <img data-src=${fullHeartIconPath} class="lazy heart hidden filled-heart-icon-circle" data-building-id=${
    building[k.id]
  } alt="Unsave heart" />
  `;
}

function buildOpenDateTag(building) {
  let tag = '';
  if (building[k.is_not_open] && building[k.open_date]) {
    const { locale } = varsFromServer;
    const options = {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    };
    const date = new Date(building[k.open_date]).toLocaleString(locale, options);
    tag = `<div class="ray-tag building-card__tag">${__('openDateTag', { date })}</div>`;
  } else if (building[k.is_not_open]) {
    tag = `<div class="ray-tag building-card__tag">${__('justAnnounced')}</div>`;
  }
  return tag;
}

function buildOnDemandTag(building) {
  if (!building[k.is_on_demand_enabled] || building[k.is_not_open]) return '';

  return `
    <div class='on-demand-tag-holder'>
      <span class="ray-tag building-card__tag on-demand-tag">${__('onDemand')}</span>
      <div class="on-demand-tag-copy">${__('onDemandTooltip')}</div>
    </div>
  `;
}

function buildDedicatedDesksPrice(building, filtersValues) {
  if (filtersValues.workspace !== 'dedicatedDesk') return '';

  const hasDedicatedDesk = filtersValues.capacity === '1' && building[k.dedicated_desk_date] <= filtersValues.date;
  return hasDedicatedDesk ? buildPriceRow(building, filtersValues, DEDICATED_DESKS, 'dedicated_desk_price') : '';
}

function buildHotDeskPrice(building, filtersValues) {
  if (filtersValues.workspace !== 'hotDesk') return '';

  const hasHotDesk = filtersValues.capacity === '1' && building[k.hot_desk_date] <= filtersValues.date;
  return hasHotDesk ? buildPriceRow(building, filtersValues, HOT_DESKS, 'hot_desk_price') : '';
}

function buildOfficePrice(building, filtersValues) {
  if (filtersValues.workspace !== 'privateOffice') return '';

  const officeCapacity = building.imperfectCapacity || filtersValues.capacity;
  const officeDate = building.imperfectDate || filtersValues.date;
  const hasOffice = building[k[`office_${officeCapacity}_date`]] <= officeDate;
  if (!hasOffice) return '';

  return buildPriceRow(building, filtersValues, PRIVATE_OFFICES, `office_${officeCapacity}_price`);
}

function getOfficePrice(building, filtersValues) {
  const officeCapacity = building.imperfectCapacity || filtersValues.capacity;
  const officeDate = building.imperfectDate || filtersValues.date;
  const officePrice =
    building[k[`office_${officeCapacity}_date`]] <= officeDate && building[k[`office_${officeCapacity}_price`]];

  return officePrice;
}

function getDedicatedDeskPrice(building, filtersValues) {
  const dedicatedDeskPrice =
    building[k.dedicated_desk_date] <= filtersValues.date &&
    filtersValues.capacity === '1' &&
    building[k.dedicated_desk_price];

  return dedicatedDeskPrice;
}

function getHotDeskPrice(building, filtersValues) {
  const hotDeskPrice =
    building[k.hot_desk_date] <= filtersValues.date && filtersValues.capacity === '1' && building[k.hot_desk_price];

  return hotDeskPrice;
}

function buildAvailableWorkspacePrice(building, filtersValues) {
  if (filtersValues.workspace) return '';

  const workspacePrices = [
    getHotDeskPrice(building, filtersValues),
    getDedicatedDeskPrice(building, filtersValues),
    getOfficePrice(building, filtersValues)
  ].filter(price => price);
  const [price] = workspacePrices;
  const minPriceText = price ? __('fromPricePerMonth', { price }) : buildViewInventory();

  return `
    <div class="building-price-row no-workspace-selected">
      <div class="price-text">Available workspace</div>
      <div class="price-value">${minPriceText}</div>
    </div>
  `;
}

function buildAmenities(building) {
  const rawAmenities = building[k.amenities];
  // mapbox stringifies the amenities, other places dont need to parse
  const amenities = typeof rawAmenities === 'string' ? JSON.parse(rawAmenities) : rawAmenities || [];

  const showCount = amenities.length <= 5 ? 5 : 4; // don't want to have a +1 tag
  const amenitiesToShow = amenities.slice(0, showCount);
  const amenitiesToCompact = amenities.slice(showCount);
  let remainderTag = '';
  if (amenitiesToCompact.length > 0) {
    remainderTag = `
      <span class="amenity-tag" title="${amenitiesToCompact.join(', ')}">
        ${amenitiesToCompact.length}+
      </span>
    `;
  }

  return `
    <div class="amenities">
      ${amenitiesToShow.map(amenity => `<span class="amenity-tag">${amenity}</span>`).join('')}
      ${remainderTag}
    </div>
  `;
}

function buildPrices(building, filtersValues, needsToFilter) {
  if (!needsToFilter) {
    return '';
  }

  return `
      <div class="building-pricing">
        ${buildAvailableWorkspacePrice(building, filtersValues)}
        ${buildOfficePrice(building, filtersValues)}
        ${buildDedicatedDesksPrice(building, filtersValues)}
        ${buildHotDeskPrice(building, filtersValues)}
      </div>
    `;
}

// @VisibleForTesting
export function buildPriceRow(building, filtersValues, type, priceKey) {
  const price = building[k[priceKey]];
  const priceMessageKey = type !== HOT_DESKS ? 'fromPricePerMonth' : 'pricePerMonth';
  const priceValue = price ? __(priceMessageKey, { price }) : buildViewInventory();
  const product = __(productTypeToMessageKeyMap[type]);
  const shortDate = ISODateToLocalizedShortDate(building.imperfectDate || filtersValues.date);
  const capacityText = building.imperfectCapacity || filtersValues.capacity;

  return `
    <div class="building-price-row">
      <div class="price-description">
        <div class="price-text">${product}</div>
        <div class="ray-tag ray-tag--with-icon capacity-tag">${capacityText}</div>
        <div class="ray-tag ray-tag--with-icon date-tag">${shortDate}</div>
      </div>
      <div class="price-value">${priceValue}</div>
    </div>
  `;
}

function buildViewInventory() {
  return `
    <span class="price-value-amount">
      ${__('viewInventory')}
    </span>
  `;
}
